import { Component, inject } from '@angular/core';
import { LayoutService } from './service/app.layout.service';
import { AuthService } from '@core/services/auth/auth.service';
import { Router } from '@angular/router';
import { User } from '@core/models';
import { environment } from '@env/environment';

enum EHotelRequestType {
  Add = 'add',
  Remove = 'remove',
  Support = 'support',
  Feedback = 'feedback',
}

@Component({
  selector: 'app-profilemenu',
  templateUrl: './app.profilesidebar.component.html',
  styleUrls: ['app.profilesidebar.component.scss'],
  standalone: false,
})
export class AppProfileSidebarComponent {
  layoutService = inject(LayoutService);
  private router = inject(Router);
  private authService = inject(AuthService);

  user: User | null;
  protected readonly EHotelRequestType = EHotelRequestType;

  constructor() {
    this.user = this.authService.user;
  }

  get name(): string {
    if (!this.user) return '';

    const firstName = this.user.firstName || this.user.first_name || '';
    const lastName = this.user.lastName || this.user.last_name || '';

    if (!firstName || !lastName) return 'CA'; // Default initials

    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }

  get visible(): boolean {
    return this.layoutService.state.profileSidebarVisible;
  }

  set visible(_val: boolean) {
    this.layoutService.state.profileSidebarVisible = _val;
  }

  get isSuperAdmin(): boolean {
    return this.user?.is_superuser || false;
  }

  get externalAdminUrl(): string {
    const host = window.location.hostname;
    if (host.includes('app.cogwheelanalytics.com')) {
      return 'https://admin-app.cogwheelanalytics.com';
    }
    if (host.includes('staging.cogwheelanalytics.com')) {
      return 'https://admin-staging.cogwheelanalytics.com';
    }
    if (host.includes('develop.cogwheelanalytics.com')) {
      return 'https://admin-develop.cogwheelanalytics.com';
    }
    // Default fallback
    return 'https://admin-app.cogwheelanalytics.com';
  }

  onDrawerHide() {
    this.layoutService.layoutState.update((state) => ({
      ...state,
      profileSidebarVisible: false,
    }));
  }

  logout(): void {
    this.authService.logout();
    this.visible = false;
    this.router.navigateByUrl('/login');
  }

  openKnowledgeBase() {
    window.open(environment.knowledgeBase.cogwheelAnalytic);
  }

  hotelRequestAndFeedback(request: EHotelRequestType) {
    let url: string;
    switch (request) {
      case EHotelRequestType.Add:
        url = environment.hotelRequest.addHotel;
        break;
      case EHotelRequestType.Remove:
        url = environment.hotelRequest.removeHotel;
        break;
      case EHotelRequestType.Support:
        url = environment.hotelRequest.supportForm;
        break;
      case EHotelRequestType.Feedback:
        url = environment.hotelRequest.feedbackForm;
        break;
      default:
        console.error('Invalid request type:', request);
        return;
    }
    window.open(url);
  }

  openExternalAdminPanel(): void {
    // Get current JWT token from auth service
    const token = this.authService.token?.access_token;

    if (!token) {
      console.error('No authentication token available');
      return;
    }

    // Construct URL with token as query parameter
    // Use the dynamic externalAdminUrl getter to determine correct environment URL
    const adminUrl = this.externalAdminUrl;
    const urlWithToken = `${adminUrl}/login?authToken=${encodeURIComponent(token)}`;

    // Open admin panel in new tab with token
    window.open(urlWithToken, '_blank', 'noopener,noreferrer');
  }
}
